import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";

/**
 * Prepare post content for rendering.
 * - Pure HTML (starts with <)  → return as-is for dangerouslySetInnerHTML
 * - Mixed (plain text + HTML appended) → convert plain-text prefix to <p> tags,
 *   keep HTML suffix as-is, return combined string
 * - Pure plain text → return null (caller uses pre-line <p> fallback)
 */
function prepareHtml(content) {
  const t = (content || '').trimStart();
  if (!t) return null;

  // Pure HTML
  if (t.startsWith('<')) return t;

  // Detect embedded HTML — closing tag or one of our pr- classes
  const htmlMatch = t.match(/<\/[a-zA-Z]|class="pr-/);
  if (!htmlMatch) return null; // Pure plain text

  const splitIdx = t.indexOf(htmlMatch[0]);
  // Walk back to start of the containing HTML tag
  const tagStart = t.lastIndexOf('<', splitIdx);
  const cutAt = tagStart >= 0 ? tagStart : splitIdx;

  const plainPart = t.substring(0, cutAt);
  const htmlPart  = t.substring(cutAt);

  // Convert plain-text paragraphs to <p> tags
  const htmlFromPlain = plainPart
    .split(/\n{2,}/)
    .map(para => para.trim())
    .filter(Boolean)
    .map(para => `<p style="white-space:pre-line;line-height:1.95;font-size:.97rem;color:var(--dk);margin-bottom:1.35rem">${para}</p>`)
    .join('\n');

  return htmlFromPlain + '\n' + htmlPart;
}

export default function PostPage() {
  const { id }     = useParams();
  const { posts }  = useApp();
  const navigate   = useNavigate();
  const post       = posts.find(p => p.id === id);

  if (!post) return <Navigate to="/news" replace/>;

  const tagClass = post.category === "Announcement" ? "tg"
    : post.category === "Event" ? "tb" : "to";

  const richHtml = prepareHtml(post.content);

  return (
    <div>
      <div className="pg-hdr">
        <span className={`tag ${tagClass}`} style={{ marginBottom: ".75rem" }}>{post.category}</span>
        <h1 style={{ maxWidth: 760, margin: "0 auto .75rem" }}>{post.title}</h1>
        <p style={{ fontSize: ".85rem" }}>
          {new Date(post.date).toLocaleDateString("en-PG", { year:"numeric", month:"long", day:"numeric" })}
        </p>
      </div>
      <section className="sec">
        <div className="container" style={{ maxWidth: 760 }}>
          <button className="back-btn" onClick={() => navigate("/news")}>← Back to News</button>
          <div style={{ background:"#fff", border:"1.5px solid var(--br)", borderRadius:16, padding:"2.5rem", boxShadow:"var(--sh)" }}>
            {post.image && <img className="post-img" src={post.image} alt={post.title}/>}
            {/* Rich HTML or mixed content → dangerouslySetInnerHTML;
                pure plain text → pre-line whitespace rendering */}
            {richHtml
              ? <div className="post-rich" dangerouslySetInnerHTML={{ __html: richHtml }}/>
              : <p style={{ whiteSpace:"pre-line", lineHeight:1.95, fontSize:".97rem", color:"var(--dk)" }}>{post.content}</p>
            }
          </div>
        </div>
      </section>
    </div>
  );
}
